import { useMemo } from "react";

import { useContainers, useLocationTree } from "@/api/queries";
import type { LocationNode, Uuid } from "@/api/types";
import { Field, Select } from "@/components/Input";

interface Flat {
  id: string;
  code: string;
  name: string;
  depth: number;
}

function flatten(nodes: LocationNode[], depth = 0): Flat[] {
  return nodes.flatMap((node) => [
    { id: node.id, code: node.code, name: node.name, depth },
    ...flatten(node.children, depth + 1),
  ]);
}

/**
 * Where the item is going.
 *
 * A location or a container, never both — the same rule the backend enforces,
 * expressed here as a single control so it cannot be got wrong.
 */
export function PlacePicker({
  locationId,
  containerId,
  onChange,
  label = "Emplacement",
}: {
  locationId: Uuid | null;
  containerId: Uuid | null;
  onChange: (place: { location_id: Uuid | null; container_id: Uuid | null }) => void;
  label?: string;
}) {
  const { data: tree } = useLocationTree();
  const { data: containers } = useContainers({});
  const locations = useMemo(() => flatten(tree ?? []), [tree]);

  const value = containerId ? `container:${containerId}` : locationId ? `location:${locationId}` : "";

  return (
    <Field label={label} htmlFor="place-picker" hint="Un emplacement ou un contenant.">
      <Select
        id="place-picker"
        value={value}
        onChange={(event) => {
          const [kind, id] = event.target.value.split(":");
          if (kind === "container") onChange({ location_id: null, container_id: id ?? null });
          else if (kind === "location") onChange({ location_id: id ?? null, container_id: null });
          else onChange({ location_id: null, container_id: null });
        }}
      >
        <option value="">— Choisir —</option>
        {locations.length > 0 && (
          <optgroup label="Emplacements">
            {locations.map((location) => (
              <option key={location.id} value={`location:${location.id}`}>
                {"— ".repeat(location.depth)}
                {location.name} ({location.code})
              </option>
            ))}
          </optgroup>
        )}
        {containers && containers.length > 0 && (
          <optgroup label="Contenants">
            {containers.map((container) => (
              <option key={container.id} value={`container:${container.id}`}>
                {container.name ?? container.container_code} ({container.container_code})
              </option>
            ))}
          </optgroup>
        )}
      </Select>
    </Field>
  );
}
